<template>
  <div class="pgm-budget">
    <!-- 전체 로딩 오버레이 -->
    <div v-if="initialLoading" class="initial-loading-overlay">
      <div class="loading-spinner">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="48"
          width="4"
        ></v-progress-circular>
        <p>예산 데이터를 불러오는 중...</p>
      </div>
    </div>

    <!-- 메인 컨텐츠 (로딩 완료 후 fade-in) -->
    <transition name="content-fade">
      <div v-show="!initialLoading" class="budget-layout" :class="{ 'sidebar-right': sidebarPosition === 'right' }">
        <!-- 고정 사이드바 (리모컨) -->
        <aside class="budget-sidebar" v-if="hasRevenueData">
          <!-- 위치 토글 버튼 -->
          <button 
            class="sidebar-position-toggle"
            @click="toggleSidebarPosition"
            :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
          >
            <v-icon size="14">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
          </button>

          <!-- 연도 선택 영역 -->
          <div class="sidebar-year-selector">
            <button 
              class="year-nav-btn" 
              @click="changeYear(-1)"
              :disabled="parseInt(searchParams.lebYear) <= minYear"
            >
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <div class="year-display">
              <span class="year-value">{{ searchParams.lebYear }}</span>
              <span class="year-unit">년</span>
            </div>
            <button 
              class="year-nav-btn" 
              @click="changeYear(1)"
              :disabled="parseInt(searchParams.lebYear) >= maxYear"
            >
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <!-- 추경차수 표시 -->
          <div class="sidebar-budget-class" :class="budgetClassStyle">
            <v-icon size="12">{{ budgetClassIcon }}</v-icon>
            <span>{{ budgetClassLabel }}</span>
          </div>

          <!-- 세입/세출 필터 -->
          <div class="sidebar-filter">
            <div class="filter-header">
              <v-icon size="14" color="#64748b">mdi-filter-variant</v-icon>
              <span>필터</span>
            </div>
            <div class="filter-buttons">
              <button 
                class="filter-btn" 
                :class="{ active: selectedTrcode === 'ALL' }"
                @click="selectedTrcode = 'ALL'; fetchFinanceCodes()"
              >전체</button>
              <button 
                class="filter-btn revenue" 
                :class="{ active: selectedTrcode === 'IN' }"
                @click="selectedTrcode = 'IN'; fetchFinanceCodes()"
              >세입</button>
              <button 
                class="filter-btn expense" 
                :class="{ active: selectedTrcode === 'OUT' }"
                @click="selectedTrcode = 'OUT'; fetchFinanceCodes()"
              >세출</button>
            </div>
          </div>

          <!-- 세입/세출 요약 -->
          <div class="sidebar-summary">
            <div class="summary-item revenue">
              <div class="summary-header">
                <span class="dot"></span>
                <span class="label">세입</span>
              </div>
              <span class="value">{{ formatMoney(totalRevenue) }}원</span>
            </div>
            <div class="summary-item expense">
              <div class="summary-header">
                <span class="dot"></span>
                <span class="label">세출</span>
              </div>
              <span class="value">{{ formatMoney(totalExpense) }}원</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-item balance" :class="{ warning: balance !== 0 }">
              <div class="summary-header">
                <v-icon size="14" :color="balance === 0 ? '#10b981' : '#ef4444'">
                  {{ balance === 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
                <span class="label">잔액</span>
              </div>
              <span class="value">{{ formatMoney(balance) }}원</span>
            </div>
          </div>

          <!-- 인건비 지출 비율 -->
          <div class="sidebar-labor-cost" v-if="totalExpense > 0">
            <div class="labor-cost-header">
              <v-icon size="16" color="#374151">mdi-account-cash</v-icon>
              <span>인건비 비율</span>
            </div>
            <div class="labor-cost-content">
              <div class="labor-cost-item standard">
                <span class="labor-cost-label">기준</span>
                <span class="labor-cost-value">{{ standardLaborCostRate }}%</span>
              </div>
              <v-tooltip location="bottom" max-width="280">
                <template v-slot:activator="{ props: tooltipProps }">
                  <div 
                    v-bind="tooltipProps"
                    class="labor-cost-item current" 
                    :class="{ warning: isLaborCostBelowStandard, safe: !isLaborCostBelowStandard }"
                  >
                    <span class="labor-cost-label">현재</span>
                    <span class="labor-cost-value">{{ currentLaborCostRate.toFixed(1) }}%</span>
                  </div>
                </template>
                <div class="labor-tooltip-content">
                  <div class="tooltip-title">산출근거</div>
                  <div class="tooltip-row">
                    <span class="tooltip-label">인건비 지출</span>
                    <span class="tooltip-value">{{ formatMoney(totalLaborCost) }}원</span>
                  </div>
                  <div class="tooltip-note">(요양보호사·사회복지사 급여+보험+퇴직금)</div>
                  <div class="tooltip-row">
                    <span class="tooltip-label">수입</span>
                    <span class="tooltip-value">{{ formatMoney(laborCostBaseRevenue) }}원</span>
                  </div>
                  <div class="tooltip-note">(장기요양급여+본인부담금+가산금)</div>
                  <div class="tooltip-formula">
                    비율 = {{ formatMoney(totalLaborCost) }} ÷ {{ formatMoney(laborCostBaseRevenue) }} × 100 = {{ currentLaborCostRate.toFixed(1) }}%
                  </div>
                </div>
              </v-tooltip>
            </div>
            <div class="labor-cost-status" :class="{ warning: isLaborCostBelowStandard }">
              <v-icon size="12" :color="isLaborCostBelowStandard ? '#ef4444' : '#10b981'">
                {{ isLaborCostBelowStandard ? 'mdi-alert' : 'mdi-check-circle' }}
              </v-icon>
              <span v-if="isLaborCostBelowStandard">기준 미달</span>
              <span v-else>기준 충족</span>
            </div>
          </div>

          <!-- 액션 버튼들 -->
          <div class="sidebar-actions">
            <button 
              class="action-btn save"
              @click="showSaveConfirmDialog = true"
              :disabled="saving"
            >
              <v-icon size="16">mdi-content-save</v-icon>
              <span>{{ saving ? '저장 중...' : '저장' }}</span>
            </button>
            <button 
              class="action-btn delete"
              @click="showDeleteDialog = true"
            >
              <v-icon size="16">mdi-delete</v-icon>
              <span>삭제</span>
            </button>
          </div>

          <!-- 엑셀 다운로드 버튼들 -->
          <div class="sidebar-excel-actions">
            <div class="excel-section-header">
              <v-icon size="14" color="#64748b">mdi-file-excel</v-icon>
              <span>엑셀 다운로드</span>
            </div>
            <button 
              class="action-btn excel"
              @click="downloadPayrollList"
              :disabled="!hasLaborCostData"
            >
              <v-icon size="16">mdi-download</v-icon>
              <span>임직원보수일람표</span>
            </button>
            <button 
              class="action-btn excel hope"
              @click="downloadHopeExcel"
              :disabled="processedData.length === 0"
            >
              <v-icon size="16">mdi-download</v-icon>
              <span>예산(산출근거포함)</span>
            </button>
          </div>
        </aside>

        <!-- 임직원보수일람표 엑셀 컴포넌트 -->
        <PgmBudgetPayExcel
          v-model="showPayExcelDialog"
          :year="searchParams.lebYear"
          :budget-data="processedData"
          :tax-data="props.taxData"
          :biz-gubun="store.state.user?.m_sisul_gubun"
          @show-snackbar="(msg, type) => showSnackbar(msg, type as 'success' | 'error' | 'warning' | 'info')"
        />

        <!-- 희망이음 예산업로드 엑셀 컴포넌트 -->
        <PgmBudgetHopeExcel
          v-model="showHopeExcelDialog"
          :year="searchParams.lebYear"
          :budget-data="processedData"
          :biz-gubun="store.state.user?.m_sisul_gubun"
          @show-snackbar="(msg, type) => showSnackbar(msg, type as 'success' | 'error' | 'warning' | 'info')"
        />

      <!-- 우측 메인 컨텐츠 -->
      <main class="budget-main-content" :class="{ 'full-width': !hasRevenueData }">
        <v-card class="table-card">
          <v-card-text class="table-card-text">
        <!-- 예산 데이터가 없을 때 예산 작성하기 버튼 -->
        <div v-if="showBudgetStarterPrompt" class="no-budget-container">
          <v-icon size="64" color="primary">mdi-file-document-edit-outline</v-icon>
          <p class="no-budget-text">{{ searchParams.lebYear }}년 예산 데이터가 없습니다</p>
          <v-btn
            color="primary"
            size="x-large"
            variant="elevated"
            @click="openBudgetWizard"
            class="create-budget-btn"
          >
            <v-icon size="24">mdi-plus-circle</v-icon>
            <span class="ml-2">예산 작성하기</span>
          </v-btn>
        </div>

        <!-- 재무코드 테이블 -->
        <div class="finance-table-wrapper" v-else-if="!loading && processedData.length > 0">
          <table class="finance-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>관</th>
                <th>항</th>
                <th>목</th>
                <th>내용</th>
                <th>단가</th>
                <th>명(회)</th>
                <th>개월</th>
                <th>금액</th>
                <th>자금원천</th>
                <th>기능</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, index) in processedData" :key="index">
                <tr>
                  <!-- 구분 (세입/세출) - rowspan 적용 -->
                  <td 
                    v-if="item.showTrcode"
                    :rowspan="item.trcodeRowspan"
                    :class="['trcode-cell', item.fc_trcode === 'IN' ? 'income' : 'expense']"
                  >
                    {{ item.fc_trname }}
                  </td>

                  <!-- 관 (L코드) - rowspan 적용 -->
                  <td 
                    v-if="item.showLcode"
                    :rowspan="item.lcodeRowspan"
                    :class="['lcode-cell', item.fc_trcode === 'IN' ? 'income' : 'expense']"
                  >
                    <div class="name-label">{{ item.fc_lname }}</div>
                    <div class="budget-sum" v-if="getLcodeBudgetSum(item) > 0">
                      {{ formatMoney(getLcodeBudgetSum(item)) }}원
                    </div>
                  </td>

                  <!-- 항 (M코드) - rowspan 적용 -->
                  <td 
                    v-if="item.showMcode"
                    :rowspan="item.mcodeRowspan"
                    class="mcode-cell"
                  >
                    <div class="name-label">{{ item.fc_mname }}</div>
                    <div class="budget-sum" v-if="getMcodeBudgetSum(item) > 0">
                      {{ formatMoney(getMcodeBudgetSum(item)) }}원
                    </div>
                  </td>

                  <!-- 목 (S코드) - rowspan 적용 -->
                  <td 
                    v-if="item.showScode"
                    :rowspan="item.scodeRowspan"
                    class="scode-cell"
                  >
                  <div class="name-label">{{ item.fc_sname }}</div>
                  <div class="budget-cell" v-if="getScodeBudgetSum(item) > 0">{{ formatMoney(getScodeBudgetSum(item)) }}원</div>
                      
                  </td>

                  <!-- 내용 -->
                  <td class="description-cell">
                    <!-- 급여 항목인 경우 셀렉트 리스트 -->
                    <select
                      v-if="item.isSalaryItem"
                      v-model="item.description"
                      class="salary-select"
                      :class="{ 'linked-highlight': isLinkedHighlighted(item) }"
                      @change="handleSalaryTypeChange(item)"
                      @focus="handleSalaryFocus(item)"
                      @blur="handleSalaryBlur()"
                    >
                      <option 
                        v-for="opt in getSalaryOptions(item.fc_scode)" 
                        :key="opt.value" 
                        :value="opt.value"
                      >{{ opt.label }}</option>
                    </select>
                    <!-- 본인부담금/장기요양급여 항목인 경우 수가 선택 + 횟수 -->
                    <div v-else-if="isRevenuePairItem(item)" class="suga-calc-wrapper">
                      <select
                        v-model="item.suga_rank"
                        class="suga-select"
                        :class="{ 'revenue-pair-highlight': isRevenuePairHighlighted(item) }"
                        @change="handleSugaChange(item)"
                        @focus="handleRevenuePairFocus(item)"
                        @blur="handleRevenuePairBlur()"
                      >
                        <option 
                          v-for="suga in props.sugaList" 
                          :key="suga.suga_idx" 
                          :value="suga.suga_rank"
                        >{{ suga.suga_rank }}</option>
                      </select>
                      <span class="suga-multiply">×</span>
                      <input 
                        type="text" 
                        v-model="item.suga_times"
                        class="suga-times-input"
                        :class="{ 'revenue-pair-highlight': isRevenuePairHighlighted(item) }"
                        @input="handleSugaTimesInput($event, item)"
                        @focus="handleRevenuePairFocus(item)"
                        @blur="handleRevenuePairBlur()"
                      />
                    </div>
                    <!-- 일반 항목인 경우 인풋 -->
                    <input 
                      v-else
                      type="text" 
                      v-model="item.description" 
                      class="description-input"
                      :disabled="item.isLinkedItem"
                      :class="{ 
                        'readonly-input': item.isLinkedItem,
                        'linked-highlight': isLinkedHighlighted(item)
                      }"
                    />
                  </td>

                  <!-- 단가 -->
                  <td class="unit-price-cell">
                    <input 
                      type="text" 
                      v-model="item.unit_price" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item) || isRevenuePairItem(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item) || isRevenuePairItem(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'unit_price') : formatNumber($event, item, 'unit_price')"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 명(회) -->
                  <td class="count-cell">
                    <input 
                      type="text" 
                      v-model="item.count" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'count') : (isRevenuePairItem(item) ? handleRevenuePairInput($event, item, 'count') : formatNumber($event, item, 'count'))"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 개월 -->
                  <td class="month-cell">
                    <input 
                      type="text" 
                      v-model="item.months" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'months') : (isRevenuePairItem(item) ? handleRevenuePairInput($event, item, 'months') : formatNumber($event, item, 'months'))"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 금액 (자동계산) -->
                  <td class="amount-cell">
                    {{ calculateAmount(item) }}
                  </td>

                  <!-- 자금원천 -->
                  <td class="funding-cell">
                    {{ item.fc_funding_name || item.fc_funding || '-' }}
                  </td>

                  <!-- 기능 -->
                  <td class="function-cell">
                    <!-- 연결된 항목 (사회보험/퇴직금) -->
                    <div v-if="item.isLinkedItem" class="linked-buttons">
                      <v-switch
                        :model-value="item.hb_pay_rel_manual === 'Y'"
                        @update:model-value="toggleManualEdit(item)"
                        density="compact"
                        hide-details
                        color="warning"
                        class="manual-switch"
                      >
                        <template v-slot:label>
                          <span class="switch-label">{{ item.hb_pay_rel_manual === 'Y' ? '수동' : '자동' }}</span>
                        </template>
                      </v-switch>
                    </div>
                    <!-- 일반 항목 (급여 포함) -->
                    <div v-else class="function-buttons">
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click.stop="addRow(index)"
                        class="add-btn"
                      >
                        <v-icon size="16">mdi-plus</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="deleteRow(index)"
                        :disabled="!canDeleteRow(item)"
                        class="delete-btn"
                      >
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- 로딩 -->
        <div v-else-if="loading" class="loading-container">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p>데이터를 불러오는 중입니다...</p>
        </div>

        <!-- 데이터 없음 (세출일 때) -->
        <div v-else-if="selectedTrcode === 'OUT'" class="no-data-container">
          <v-icon size="48" color="grey">mdi-database-alert</v-icon>
          <p>조회할 데이터가 없습니다.</p>
        </div>
          </v-card-text>
        </v-card>
      </main>
      </div>
    </transition>

    <!-- 단계별 예산 작성 모달 -->
    <v-dialog v-model="showBudgetWizard" max-width="600px" >
      <v-card class="budget-wizard-card">
        <v-card-title class="wizard-header">
          <div class="wizard-title-section">
            <v-icon size="28" color="primary" class="wizard-icon">mdi-file-document-edit</v-icon>
            <div>
              <h3 class="wizard-title">예산 작성하기</h3>
              <p class="wizard-subtitle">단계 {{ currentStep }} / {{ totalSteps }}</p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            @click="closeBudgetWizard"
            class="close-wizard-btn"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="wizard-content">
          <!-- 진행 바 -->
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: `${(currentStep / totalSteps) * 100}%` }"></div>
          </div>

          <!-- 단계 1: 수급자 수 -->
          <div v-if="currentStep === 1" class="wizard-step">
            <div class="step-icon-wrapper">
              <v-icon size="48" color="primary">mdi-account-heart</v-icon>
            </div>
            <h4 class="step-question">수급자는 몇 명인가요?</h4>
            <p class="step-description">예산 작성을 위해 수급자 수를 입력해주세요.</p>
            <v-text-field
              v-model="firstBudgetData.patientCount"
              type="number"
              variant="outlined"
              density="comfortable"
              placeholder="수급자 수를 입력하세요"
              min="1"
              hide-details
              class="step-input"
              @input="onPatientCountInput"
            />
          </div>

          <!-- 단계 2: 요양보호사 수 -->
          <div v-if="currentStep === 2" class="wizard-step">
            <div class="step-icon-wrapper">
              <v-icon size="48" color="primary">mdi-account-group</v-icon>
            </div>
            <h4 class="step-question">요양보호사는 몇 명인가요?</h4>
            <p class="step-description">예산 작성을 위해 요양보호사 수를 입력해주세요.</p>
            <v-text-field
              v-model="firstBudgetData.caregiverCount"
              type="number"
              variant="outlined"
              density="comfortable"
              placeholder="요양보호사 수를 입력하세요"
              min="1"
              hide-details
              class="step-input"
              @input="onCaregiverCountInput"
            />
          </div>

          <!-- 단계 3: 수가 평균 -->
          <div v-if="currentStep === 3" class="wizard-step">
            <div class="step-icon-wrapper">
              <v-icon size="48" color="primary">mdi-clock-outline</v-icon>
            </div>
            <h4 class="step-question">수가 평균을 선택해주세요</h4>
            <p class="step-description">예산 작성을 위해 수가 평균을 선택해주세요.</p>
            <select 
              v-model="firstBudgetData.selectedSugaTime" 
              @change="onSugaTimeChange"
              class="suga-time-select"
            >
              <option value="">시간을 선택하세요</option>
              <option 
                v-for="suga in props.sugaList" 
                :key="suga.suga_idx" 
                :value="suga.suga_rank"
              >
                {{ suga.suga_rank }} - {{ formatMoney(Number(suga.suga_money)) }}원
              </option>
            </select>
          </div>

          <!-- 단계 4: 예산 개월 수 -->
          <div v-if="currentStep === 4" class="wizard-step">
            <div class="step-icon-wrapper">
              <v-icon size="48" color="primary">mdi-calendar-month</v-icon>
            </div>
            <h4 class="step-question">예산 개월 수를 입력해주세요</h4>
            <p class="step-description">예산 작성을 위해 예산 개월 수를 입력해주세요. (1~12개월)</p>
            <v-text-field
              v-model="firstBudgetData.budgetMonths"
              type="number"
              variant="outlined"
              density="comfortable"
              placeholder="예산 개월 수를 입력하세요"
              min="1"
              max="12"
              hide-details
              class="step-input"
              @input="onBudgetMonthsInput"
            />
          </div>
        </v-card-text>

        <v-card-actions class="wizard-actions">
          <v-btn
            v-if="currentStep > 1"
            variant="outlined"
            @click="prevStep"
            class="wizard-btn prev-btn"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
            <span class="ml-1">이전</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="currentStep < totalSteps"
            color="primary"
            variant="elevated"
            @click="nextStep"
            :disabled="!isCurrentStepValid"
            class="wizard-btn next-btn"
          >
            <span class="mr-1">다음</span>
            <v-icon size="18">mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            v-else
            color="primary"
            variant="elevated"
            @click="createFirstBudget"
            :disabled="!isFirstBudgetFormValid"
            :loading="saving"
            class="wizard-btn submit-btn"
          >
            <v-icon size="18">mdi-check</v-icon>
            <span class="ml-1">예산 생성하기</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 예산 삭제 확인 다이얼로그 (전체 예산 삭제) -->
    <v-dialog v-model="showDeleteDialog" max-width="450px">
      <v-card class="delete-dialog-card">
        <v-card-title class="delete-dialog-header">
          <v-icon size="24" color="error" class="mr-2">mdi-alert-circle</v-icon>
          <span>예산 삭제 확인</span>
        </v-card-title>
        <v-card-text class="delete-dialog-content">
          <p class="delete-warning-text">
            <strong>{{ searchParams.lebYear }}년 {{ budgetClassLabel }}</strong> 예산 데이터를 삭제하시겠습니까?
          </p>
          <p class="delete-notice">
            삭제된 예산은 복구할 수 없습니다.
          </p>
        </v-card-text>
        <v-card-actions class="delete-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="showDeleteDialog = false"
          >
            취소
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="deleteBudget"
            :loading="deleting"
          >
            <v-icon size="18">mdi-delete</v-icon>
            <span class="ml-1">삭제</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 저장 확인 다이얼로그 -->
    <v-dialog v-model="showSaveConfirmDialog" max-width="400px">
      <v-card class="save-confirm-dialog-card">
        <v-card-title class="save-confirm-header">
          <v-icon size="24" color="primary" class="mr-2">mdi-content-save</v-icon>
          <span>저장 확인</span>
        </v-card-title>
        <v-card-text class="save-confirm-content">
          <p>{{ searchParams.lebYear }}년 {{ budgetClassLabel }} 예산을 저장하시겠습니까?</p>
          <p v-if="pendingDeleteIds.length > 0" class="delete-notice mt-2">
            <v-icon size="14" color="warning" class="mr-1">mdi-alert</v-icon>
            삭제 대기 중인 항목 {{ pendingDeleteIds.length }}개가 함께 삭제됩니다.
          </p>
        </v-card-text>
        <v-card-actions class="save-confirm-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="showSaveConfirmDialog = false"
          >
            취소
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="confirmSave"
            :loading="saving"
          >
            <v-icon size="18">mdi-check</v-icon>
            <span class="ml-1">저장</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 알림 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
    >
      <div class="snackbar-content">
        <v-icon size="20" class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          닫기
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import PgmBudgetPayExcel from "./PgmBudgetPayExcel.vue";
import PgmBudgetHopeExcel from "./PgmBudgetHopeExcel.vue";

const store = useStore();

// 수가 인터페이스 (props용)
interface SugaData {
  suga_idx: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number;
  suga_bonin: number;
  suga_wdate: string;
}

// 세금/보험 요율 인터페이스
interface TaxData {
  tr_idx?: number;
  tr_year?: string;
  tr_time_pay?: number;   // 최저시급
  tr_yeon?: number;       // 국민연금 요율
  tr_gun?: number;        // 건강보험 요율
  tr_go?: number;         // 고용보험 요율
  tr_jang?: number;       // 장기요양보험 요율
  tr_sil?: number;        // 실업급여 요율
  tr_san?: number;        // 산재보험 요율
  tr_labor_cost_rate?: number; // 인건비 지출 비율
}

// 소득세 테이블 인터페이스
interface SojiData {
  income1: number;
  income2: number;
  depe_1: number;
}

// Props
const props = defineProps<{
  year?: string;
  budgetClass?: string; // 추경차수: '' = 본예산, '1' = 1차추경, '2' = 2차추경
  sugaList?: SugaData[];
  sugaYear?: string;
  sugaIsFallback?: boolean;
  taxData?: TaxData;
  sojiData?: SojiData[];
}>();

interface FinanceCode {
  fc_idx: number;
  fc_trcode: string;
  fc_trname: string;
  fc_year: string;
  fc_lcode: string;
  fc_lname: string;
  fc_mcode: string;
  fc_mname: string;
  fc_scode: string;
  fc_sname: string;
  fc_funding: string;
  fc_hope_scode?: string;
  fc_funding_name?: string;
  budget_amount?: string;
  description?: string;
  unit_price?: string;
  count?: string;
  months?: string;
  hb_idx?: number; // 예산 데이터 고유 ID
  hb_pay_rel?: string; // 급여-보험-퇴직금 연결 키 (고유 문자열)
  hb_pay_rel_manual?: 'Y' | 'N'; // 수동 편집 모드 여부 (Y: 자동계산 안됨)
  lcode_budget_sum?: number; // 관별 예산액 합계
  mcode_budget_sum?: number; // 항별 예산액 합계
  scode_budget_sum?: number; // 목별 예산액 합계
  isSalaryItem?: boolean; // 급여 항목 여부
  isLinkedItem?: boolean; // 급여에 연결된 사회보험/퇴직금 여부
  isRevenuePairItem?: boolean; // 본인부담금-장기요양급여 쌍 항목 여부
  hb_revenue_rel?: string; // 본인부담금-장기요양급여 연결 키 (고유 문자열)
  suga_rank?: string; // 선택된 수가 등급 (예: '180분')
  suga_times?: string; // 수가 횟수 (기본값: 20)
}

interface ProcessedFinanceCode extends FinanceCode {
  showTrcode: boolean;
  showLcode: boolean;
  showMcode: boolean;
  showScode: boolean;
  trcodeRowspan: number;
  lcodeRowspan: number;
  mcodeRowspan: number;
  scodeRowspan: number;
  isAddedRow?: boolean; // 추가된 행인지 여부
  hb_pay_rel?: string; // 급여-보험-퇴직금 연결 키 (고유 문자열)
  hb_pay_rel_manual?: 'Y' | 'N'; // 수동 편집 모드 여부 (Y: 자동계산 안됨)
  isLinkedItem?: boolean; // 급여에 연결된 사회보험/퇴직금 여부
  isSalaryItem?: boolean; // 급여 항목 여부
  isRevenuePairItem?: boolean; // 본인부담금-장기요양급여 쌍 항목 여부
  hb_revenue_rel?: string; // 본인부담금-장기요양급여 연결 키 (고유 문자열)
  suga_rank?: string; // 선택된 수가 등급 (예: '180분')
  suga_times?: string; // 수가 횟수 (기본값: 20)
  // 수동편집 모드에서 입력한 값 보존용
  manual_saved_unit_price?: string;
  manual_saved_count?: string;
  manual_saved_months?: string;
}

const searchParams = ref({
  lebYear: props.year || store.state.lebYear || new Date().getFullYear().toString(),
});

const selectedTrcode = ref<string>("ALL");
const saving = ref(false);
const loading = ref(false);
const initialLoading = ref(true); // 초기 페이지 로딩 상태
const deleting = ref(false);
const showDeleteDialog = ref(false);
const showSaveConfirmDialog = ref(false); // 저장 확인 다이얼로그
const financeCodes = ref<FinanceCode[]>([]);
const processedData = ref<ProcessedFinanceCode[]>([]);
const pendingDeleteIds = ref<number[]>([]); // 저장 시 삭제할 hb_idx 목록
const focusedPayRelGroup = ref<string | null>(null); // 포커스된 급여-보험-퇴직금 그룹 키

// 알림 스낵바 상태
const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
  icon: 'mdi-check-circle',
  timeout: 3000
});

// 스낵바 표시 헬퍼 함수
const showSnackbar = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  const config = {
    success: { color: 'success', icon: 'mdi-check-circle' },
    error: { color: 'error', icon: 'mdi-alert-circle' },
    warning: { color: 'warning', icon: 'mdi-alert' },
    info: { color: 'info', icon: 'mdi-information' }
  };
  
  snackbar.value = {
    show: true,
    message,
    color: config[type].color,
    icon: config[type].icon,
    timeout: 3000
  };
};
// focusedRevenuePairGroup은 focusedRevenuePairItem으로 대체됨 (순서 기반 연동)
const sidebarPosition = ref<'left' | 'right'>('right'); // 사이드바 위치 (기본: 우측)

// 임직원보수일람표 다이얼로그 상태
const showPayExcelDialog = ref(false);
// 희망이음 예산업로드 다이얼로그 상태
const showHopeExcelDialog = ref(false);

// 급여-보험-퇴직금 연결 키 생성 (고유 키)
const generatePayRelKey = (): string => {
  return `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// 사이드바 위치 토글
const toggleSidebarPosition = () => {
  sidebarPosition.value = sidebarPosition.value === 'left' ? 'right' : 'left';
};

// 급여 관련 scode (1111: 급여(직접비), 1112: 급여(간접비))
const SALARY_SCODES = ['1111', '1112'];
// 사회보험 관련 scode (1151: 사회보험(직접비), 1152: 사회보험(간접비))
const INSURANCE_SCODES = ['1151', '1152'];
// 퇴직금 관련 scode (1141: 퇴직금(직접비), 1142: 퇴직금(간접비))
const RETIREMENT_SCODES = ['1141', '1142'];
// 본인부담금-장기요양급여 쌍 scode (112: 본인부담금수입, 611: 장기요양급여수입)
const REVENUE_PAIR_SCODES = ['112', '611'];

// 표에서 숨길 scode 목록
const HIDDEN_SCODES_IN = ['113', '114', '115', '211', '913', '1013']; // 세입
const HIDDEN_SCODES_OUT = ['311', '312', '313', '314', '317', '318', '321', '331', '332']; // 세출

// 급여 종류 셀렉트 옵션 - 직접비(1111)용
const SALARY_OPTIONS_DIRECT = [
  { value: '사회복지사 급여', label: '사회복지사 급여', isCeo: false },
  { value: '사회복지사(대표자) 급여', label: '사회복지사(대표자) 급여', isCeo: true },
  { value: '요양보호사 급여', label: '요양보호사 급여', isCeo: false },
  { value: '요양보호사(대표자) 급여', label: '요양보호사(대표자) 급여', isCeo: true },
];

// 급여 종류 셀렉트 옵션 - 간접비(1112)용
const SALARY_OPTIONS_INDIRECT = [
  { value: '센터장 급여', label: '센터장 급여', isCeo: false },
  { value: '센터장(대표자) 급여', label: '센터장(대표자) 급여', isCeo: true },
];

// scode에 따른 급여 옵션 가져오기
const getSalaryOptions = (scode: string) => {
  if (scode === '1111') {
    // 직접비: 사회복지사, 요양보호사
    return SALARY_OPTIONS_DIRECT;
  } else if (scode === '1112') {
    // 간접비: 센터장
    return SALARY_OPTIONS_INDIRECT;
  }
  return [];
};

// 급여/퇴직금/사회보험 정렬 우선순위 (센터장 > 사회복지사 > 요양보호사)
const SALARY_SORT_ORDER: { [key: string]: number } = {
  '센터장': 1,
  '센터장(대표자)': 2,
  '사회복지사': 3,
  '사회복지사(대표자)': 4,
  '요양보호사': 5,
  '요양보호사(대표자)': 6,
};

// 급여/퇴직금/사회보험 항목의 정렬 순서 가져오기
const getSalaryTypeOrder = (description: string): number => {
  if (!description) return 999;
  
  // 급여 항목
  for (const key of Object.keys(SALARY_SORT_ORDER)) {
    if (description.startsWith(key + ' ')) {
      return SALARY_SORT_ORDER[key];
    }
  }
  return 999;
};

// 첫 예산 작성 데이터
const firstBudgetData = ref({
  caregiverCount: "",
  patientCount: "",
  selectedSugaTime: "",
  budgetMonths: "12"
});

// Emits
const emit = defineEmits<{
  (e: 'open-starter'): void;
  (e: 'year-change', year: string): void;
}>();

// 예산 작성 마법사 관련
const showBudgetWizard = ref(false);
const currentStep = ref(1);
const totalSteps = 4;



// 저장 가능한 유효한 항목인지 확인 (내용, 단가, 명회, 개월이 모두 있어야 함)
const isValidForSave = (item: ProcessedFinanceCode): boolean => {
  const description = item.description?.trim() || '';
  const unitPrice = item.unit_price?.replace(/,/g, '') || '0';
  const count = item.count?.replace(/,/g, '') || '0';
  const months = item.months?.replace(/,/g, '') || '0';
  
  // 내용이 없거나, 단가/명회/개월 중 하나라도 0이면 저장 대상 아님
  if (!description) return false;
  if (Number(unitPrice) === 0 && Number(count) === 0 && Number(months) === 0) return false;
  
  return true;
};

const saveAllData = async () => {
  if (!store.state.user?.m_sisul_code) {
    console.error("시설 코드가 없습니다.");
    return;
  }

  saving.value = true;
  try {
    // 1. 삭제 대기 중인 항목들 먼저 삭제
    if (pendingDeleteIds.value.length > 0) {
      await axios.post("/api/budget/delete-multiple", {
        hbIdxList: pendingDeleteIds.value,
      });
      // 삭제 완료 후 목록 초기화
      pendingDeleteIds.value = [];
    }

    // 2. 유효한 항목만 필터링 (기존 데이터 또는 새로 입력된 유효한 데이터)
    const validItems = processedData.value.filter((item) => {
      // 기존 데이터(hb_idx가 있는 경우)는 항상 저장 (업데이트)
      if (item.hb_idx) return true;
      // 새 데이터는 유효성 검사 통과해야 저장
      return isValidForSave(item);
    });
    
    // 모든 항목 저장 (hb_idx 유무에 따라 INSERT/UPDATE 구분)
    const budgetList = validItems.map((item) => {
      // 콤마 제거하여 숫자로 변환
      const unitPrice = item.unit_price?.replace(/,/g, "") || "0";
      const count = item.count?.replace(/,/g, "") || "0";
      const months = item.months?.replace(/,/g, "") || "0";
      
      // 정확한 금액 계산 (연결된 항목은 보정된 금액 사용)
      const amount = getExactAmount(item).toString();

      // 대표자 여부 확인 (급여 항목에서 description에 "(대표자)" 포함 여부)
      const isCeo = item.isSalaryItem && item.description?.includes('(대표자)') ? 'Y' : 'N';

      return {
        hb_idx: item.hb_idx || null, // 없으면 null (INSERT)
        trcode: item.fc_trcode, // INSERT 시 필요
        scode: item.fc_scode, // INSERT 시 필요
        hb_pay_rel: item.hb_pay_rel || null,
        hb_pay_rel_manual: item.hb_pay_rel_manual || 'N',
        hb_is_ceo: isCeo,
        hb_revenue_rel: item.hb_revenue_rel || null,
        description: item.description || "",
        unit_price: unitPrice,
        count: count,
        months: months,
        amount: amount,
        fc_funding: item.fc_funding || "",
      };
    });

    // 3. 백엔드로 저장 요청
    const response = await axios.post("/api/budget/save", {
      sisulCode: store.state.user.m_sisul_code,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || '',
      budgetList: budgetList,
    });

    if (response.data.success) {
      // 저장 후 전체 표 다시 조회 (화면 유지, 데이터만 갱신)
      await fetchFinanceCodes(true);
      showSnackbar('예산이 성공적으로 저장되었습니다.', 'success');
    } else {
      console.error("저장 실패:", response.data.message);
      showSnackbar('저장에 실패했습니다: ' + (response.data.message || '알 수 없는 오류'), 'error');
    }
  } catch (error) {
    console.error("저장 오류:", error);
    showSnackbar('저장 중 오류가 발생했습니다.', 'error');
  } finally {
    saving.value = false;
  }
};

// 저장 확인 후 실제 저장 수행
const confirmSave = async () => {
  showSaveConfirmDialog.value = false;
  await saveAllData();
};

// 예산 데이터 백엔드에서 불러오기
const loadBudgetData = async () => {
  if (!store.state.user?.m_sisul_code) {
    return;
  }

  try {
    const response = await axios.get("/api/budget", {
      params: {
        sisulCode: store.state.user.m_sisul_code,
        year: searchParams.value.lebYear,
        gubun: selectedTrcode.value === "ALL" ? null : selectedTrcode.value,
        budClass: props.budgetClass || '', // 추경차수 조건 추가 (빈 문자열 = 본예산)
      },
    });

    if (response.data.success && response.data.data) {
      const budgetData = response.data.data;

      // 예산 데이터를 financeCodes에 매핑
      // 같은 scode에 여러 행이 있을 수 있으므로 먼저 그룹화
      const budgetByScodeMap = new Map<string, any[]>();
      budgetData.forEach((bd: any) => {
        const key = `${bd.hb_gubun}-${bd.hb_scode}`;
        if (!budgetByScodeMap.has(key)) {
          budgetByScodeMap.set(key, []);
        }
        budgetByScodeMap.get(key)?.push(bd);
      });

      // 새로운 financeCodes 배열 생성 (여러 행 지원)
      const newFinanceCodes: FinanceCode[] = [];
      
      financeCodes.value.forEach((fc) => {
        const key = `${fc.fc_trcode}-${fc.fc_scode}`;
        const matchingBudgets = budgetByScodeMap.get(key);

        if (matchingBudgets && matchingBudgets.length > 0) {
          // 각 예산 데이터에 대해 행 생성
          matchingBudgets.forEach((budget) => {
            const isSalary = SALARY_SCODES.includes(fc.fc_scode);
            const isInsurance = INSURANCE_SCODES.includes(fc.fc_scode);
            const isRetirement = RETIREMENT_SCODES.includes(fc.fc_scode);
            const isLinked = (isInsurance || isRetirement) && budget.hb_pay_rel;
            // 수입 쌍(112/611)은 세입(IN)에서만 유효
            const isRevenuePair = fc.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(fc.fc_scode);
            
            // 본인부담금/장기요양급여의 경우 description에서 수가 정보 파싱
            let sugaRank = '';
            let sugaTimes = '20'; // 기본값
            if (isRevenuePair && budget.hb_memo) {
              // "180분 X 20" 또는 "180분수가 X 20" 형태 파싱
              const match = budget.hb_memo.match(/^(.+?)\s*[Xx×]\s*(\d+)$/);
              if (match) {
                sugaRank = match[1].replace('수가', '').trim();
                sugaTimes = match[2];
              }
            }
            
            newFinanceCodes.push({
              ...fc,
              hb_idx: budget.hb_idx,
              hb_pay_rel: budget.hb_pay_rel || null,
              hb_pay_rel_manual: budget.hb_pay_rel_manual || 'N',
              hb_revenue_rel: budget.hb_revenue_rel || null,
              description: budget.hb_memo || "",
              unit_price: budget.hb_price ? Number(budget.hb_price).toLocaleString() : "",
              count: budget.hb_person ? Number(budget.hb_person).toLocaleString() : "",
              months: budget.hb_month ? Number(budget.hb_month).toLocaleString() : "",
              isSalaryItem: isSalary,
              isLinkedItem: isLinked,
              isRevenuePairItem: isRevenuePair,  // fc_scode 기반 (112/611)
              suga_rank: isRevenuePair ? sugaRank : undefined,
              suga_times: isRevenuePair ? sugaTimes : undefined,
            });
          });
        } else {
          // 예산 데이터가 없으면 기본 행만 추가
          // 수입 쌍(112/611)은 세입(IN)에서만 유효
          const isRevenuePair = fc.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(fc.fc_scode);
          newFinanceCodes.push({ 
            ...fc,
            isRevenuePairItem: isRevenuePair,  // fc_scode 기반 (112/611, 세입만)
            suga_rank: isRevenuePair ? '180분' : undefined,
            suga_times: isRevenuePair ? '20' : undefined,
          });
        }
      });

      financeCodes.value = newFinanceCodes;
      // 저장 후 로드 시 이전 편집 상태가 남지 않도록 초기화
      processedData.value = [];
      processFinanceCodes();
    } else {
      processFinanceCodes();
    }
  } catch (error) {
    console.error("예산 데이터 조회 오류:", error);
    processFinanceCodes();
  }
};

// props.sugaList가 변경되면 기본값 설정
watch(() => props.sugaList, (newList) => {
  if (newList && newList.length > 0) {
    // '180' 항목을 기본값으로 설정
    const defaultSuga = newList.find(suga => suga.suga_rank === '180분');
    if (defaultSuga && !firstBudgetData.value.selectedSugaTime) {
      firstBudgetData.value.selectedSugaTime = defaultSuga.suga_rank;
    }
  }
}, { immediate: true });

// 재무코드 조회 (silent: true면 로딩 표시 없이 데이터만 갱신)
const fetchFinanceCodes = async (silent: boolean = false) => {
  if (!silent) {
    loading.value = true;
    // 새로운 데이터를 가져올 때 processedData 초기화 (silent가 아닐 때만)
    processedData.value = [];
  }
  
  try {
    if (selectedTrcode.value === "ALL") {
      // 전체 조회: 세입과 세출을 모두 가져와서 합치기
      const [inResponse, outResponse] = await Promise.all([
        axios.get("/api/finance/codes", {
          params: {
            year: searchParams.value.lebYear,
            trcode: "IN",
            sisulCode: store.state.user?.m_sisul_code,
          },
        }),
        axios.get("/api/finance/codes", {
          params: {
            year: searchParams.value.lebYear,
            trcode: "OUT",
            sisulCode: store.state.user?.m_sisul_code,
          },
        }),
      ]);

      const inData = inResponse.data.success ? inResponse.data.data : [];
      const outData = outResponse.data.success ? outResponse.data.data : [];
      financeCodes.value = [...inData, ...outData];
      // 재무코드 조회 후 예산 데이터도 불러오기
      await loadBudgetData();
    } else {
      const response = await axios.get("/api/finance/codes", {
        params: {
          year: searchParams.value.lebYear,
          trcode: selectedTrcode.value,
          sisulCode: store.state.user?.m_sisul_code,
        },
      });

      if (response.data.success) {
        financeCodes.value = response.data.data;
        // 재무코드 조회 후 예산 데이터도 불러오기
        await loadBudgetData();
      } else {
        financeCodes.value = [];
        processedData.value = [];
      }
    }
  } catch (error) {
    console.error("재무코드 조회 오류:", error);
    financeCodes.value = [];
    processedData.value = [];
  } finally {
    if (!silent) {
      loading.value = false;
    }
    // 초기 로딩 완료 (약간의 딜레이로 부드러운 전환)
    if (initialLoading.value) {
      setTimeout(() => {
        initialLoading.value = false;
      }, 100);
    }
  }
};

// rowspan 계산을 위한 데이터 가공
const processFinanceCodes = () => {
  const data: ProcessedFinanceCode[] = [];
  
  // financeCodes를 기준으로 처리하되, 기존 processedData에서 편집된 데이터와 추가된 행을 병합
  const existingDataMap = new Map<number, ProcessedFinanceCode>();
  const addedRows: ProcessedFinanceCode[] = [];
  
  // 기존 processedData에서 편집된 데이터와 추가된 행 분리
  processedData.value.forEach(item => {
    if (item.isAddedRow) {
      addedRows.push(item);
    } else if (item.fc_idx) {
      existingDataMap.set(item.fc_idx, item);
    }
  });
  
  // financeCodes를 기준으로 sourceData 생성
  const sourceData: any[] = financeCodes.value.map(item => {
    const existing = existingDataMap.get(item.fc_idx);
    if (existing) {
      // 기존 편집 데이터가 있으면 그것을 사용
      return {
        ...item,
        hb_idx: existing.hb_idx || item.hb_idx,
        budget_amount: existing.budget_amount !== undefined ? existing.budget_amount : (item.budget_amount || ""),
        description: existing.description !== undefined ? existing.description : (item.description || ""),
        unit_price: existing.unit_price !== undefined ? existing.unit_price : (item.unit_price || ""),
        count: existing.count !== undefined ? existing.count : (item.count || ""),
        months: existing.months !== undefined ? existing.months : (item.months || ""),
        lcode_budget_sum: item.lcode_budget_sum || 0,
        mcode_budget_sum: item.mcode_budget_sum || 0,
        scode_budget_sum: item.scode_budget_sum || 0,
        isAddedRow: false
      };
    } else {
      return {
        ...item,
        hb_idx: item.hb_idx,
        budget_amount: item.budget_amount || "",
        description: item.description || "",
        unit_price: item.unit_price || "",
        count: item.count || "",
        months: item.months || "",
        lcode_budget_sum: item.lcode_budget_sum || 0,
        mcode_budget_sum: item.mcode_budget_sum || 0,
        scode_budget_sum: item.scode_budget_sum || 0,
        isAddedRow: false
      };
    }
  });
  
  // 추가된 행(isAddedRow: true)은 유지
  sourceData.push(...addedRows);
  
  // 숨길 scode 필터링
  const filteredSourceData = sourceData.filter(item => {
    if (item.fc_trcode === 'IN' && HIDDEN_SCODES_IN.includes(item.fc_scode)) {
      return false;
    }
    if (item.fc_trcode === 'OUT' && HIDDEN_SCODES_OUT.includes(item.fc_scode)) {
      return false;
    }
    return true;
  });

  // 급여/퇴직금/사회보험 항목 정렬 (센터장 > 사회복지사 > 요양보호사 순서)
  // 같은 scode 그룹 내에서만 정렬
  const sortedSourceData = [...filteredSourceData].sort((a, b) => {
    // 먼저 trcode, lcode, mcode, scode 순서대로 비교
    if (a.fc_trcode !== b.fc_trcode) return a.fc_trcode.localeCompare(b.fc_trcode);
    if (a.fc_lcode !== b.fc_lcode) return a.fc_lcode.localeCompare(b.fc_lcode);
    if (a.fc_mcode !== b.fc_mcode) return a.fc_mcode.localeCompare(b.fc_mcode);
    if (a.fc_scode !== b.fc_scode) return a.fc_scode.localeCompare(b.fc_scode);
    
    // 같은 scode 내에서 급여/퇴직금/사회보험 관련 항목인지 확인
    const isSalaryRelatedA = SALARY_SCODES.includes(a.fc_scode) || 
                            INSURANCE_SCODES.includes(a.fc_scode) || 
                            RETIREMENT_SCODES.includes(a.fc_scode);
    const isSalaryRelatedB = SALARY_SCODES.includes(b.fc_scode) || 
                            INSURANCE_SCODES.includes(b.fc_scode) || 
                            RETIREMENT_SCODES.includes(b.fc_scode);
    
    // 급여 관련 항목이면 description 기준 정렬
    if (isSalaryRelatedA && isSalaryRelatedB) {
      const orderA = getSalaryTypeOrder(a.description || '');
      const orderB = getSalaryTypeOrder(b.description || '');
      return orderA - orderB;
    }
    
    return 0;
  });

  // rowspan 카운트
  const trcodeGroups: { [key: string]: number } = {};
  const lcodeGroups: { [key: string]: number } = {};
  const mcodeGroups: { [key: string]: number } = {};
  const scodeGroups: { [key: string]: number } = {};

  sortedSourceData.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    trcodeGroups[trcodeKey] = (trcodeGroups[trcodeKey] || 0) + 1;
    lcodeGroups[lcodeKey] = (lcodeGroups[lcodeKey] || 0) + 1;
    mcodeGroups[mcodeKey] = (mcodeGroups[mcodeKey] || 0) + 1;
    scodeGroups[scodeKey] = (scodeGroups[scodeKey] || 0) + 1;
  });

  // 2차: 표시 여부 결정
  let prevTrcode = "";
  let prevLcode = "";
  let prevMcode = "";
  let prevScode = "";
  let trcodeCount = 0;
  let lcodeCount = 0;
  let mcodeCount = 0;
  let scodeCount = 0;

  sortedSourceData.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    const showTrcode = item.fc_trcode !== prevTrcode;
    const showLcode = lcodeKey !== `${prevTrcode}-${prevLcode}`;
    const showMcode = mcodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}`;
    const showScode = scodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}-${prevScode}`;

    if (showTrcode) {
      prevTrcode = item.fc_trcode;
      trcodeCount = 0;
    }

    if (showLcode) {
      prevLcode = item.fc_lcode;
      lcodeCount = 0;
    }

    if (showMcode) {
      prevMcode = item.fc_mcode;
      mcodeCount = 0;
    }

    if (showScode) {
      prevScode = item.fc_scode;
      scodeCount = 0;
    }

    data.push({
      ...item,
      showTrcode: trcodeCount === 0,
      showLcode: lcodeCount === 0,
      showMcode: mcodeCount === 0,
      showScode: scodeCount === 0,
      trcodeRowspan: trcodeGroups[trcodeKey],
      lcodeRowspan: lcodeGroups[lcodeKey],
      mcodeRowspan: mcodeGroups[mcodeKey],
      scodeRowspan: scodeGroups[scodeKey],
    });

    trcodeCount++;
    lcodeCount++;
    mcodeCount++;
    scodeCount++;
  });

  processedData.value = data;
};

// 숫자 포맷팅
const formatNumber = (event: any, item: any, field: string) => {
  const value = event.target.value.replace(/[^0-9]/g, "");
  item[field] = value ? Number(value).toLocaleString() : "";
};

// 금액 계산 (단가 × 명(회) × 개월)
// 연결된 항목(퇴직금, 사회보험)의 경우 정확한 총액으로 보정
const calculateAmount = (item: ProcessedFinanceCode) => {
  const unitPrice = Number(item.unit_price?.replace(/,/g, "") || 0);
  const count = Number(item.count?.replace(/,/g, "") || 0);
  const months = Number(item.months?.replace(/,/g, "") || 0);
  
  // 연결된 항목(퇴직금, 사회보험)인 경우 정확한 총액 계산
  if (item.isLinkedItem && item.hb_pay_rel && item.hb_pay_rel_manual !== 'Y') {
    // 연결된 급여 항목 찾기
    const salaryItem = processedData.value.find(
      row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
    );
    
    if (salaryItem) {
      const salaryUnitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
      const salaryCount = Number(salaryItem.count?.replace(/,/g, "") || 0);
      const salaryMonths = Number(salaryItem.months?.replace(/,/g, "") || 0);
      const isCeo = (salaryItem.description || '').includes('(대표자)');
      
      // 퇴직금인 경우: 정확한 총액 = 월 총 급여 × 개월 / 12
      if (RETIREMENT_SCODES.includes(item.fc_scode)) {
        const exactTotal = isCeo ? 0 : Math.round((salaryUnitPrice * salaryCount * salaryMonths) / 12);
        return exactTotal > 0 ? exactTotal.toLocaleString() : "-";
      }
      
      // 사회보험인 경우: 정확한 총액 = 월 총 급여 × 요율들의 합 × 개월
      if (INSURANCE_SCODES.includes(item.fc_scode) && props.taxData) {
        const totalMonthlySalary = salaryUnitPrice * salaryCount;
        
        const nationalPension = Math.round(totalMonthlySalary * (props.taxData.tr_yeon || 0));
        const healthInsurance = Math.round(totalMonthlySalary * (props.taxData.tr_gun || 0));
        const longTermCare = Math.round(healthInsurance * (props.taxData.tr_jang || 0));
        const employmentInsurance = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_go || 0));
        const unemployment = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_sil || 0));
        const industrialAccident = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_san || 0));
        
        const monthlyInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
        const exactTotal = monthlyInsurance * salaryMonths;
        return exactTotal > 0 ? exactTotal.toLocaleString() : "-";
      }
    }
  }
  
  // 일반 항목: 단순 계산
  const amount = unitPrice * count * months;
  return amount > 0 ? amount.toLocaleString() : "-";
};

// 정확한 금액 계산 (숫자 반환) - 연결된 항목의 경우 보정된 금액 반환
const getExactAmount = (item: ProcessedFinanceCode): number => {
  const unitPrice = Number(item.unit_price?.replace(/,/g, "") || 0);
  const count = Number(item.count?.replace(/,/g, "") || 0);
  const months = Number(item.months?.replace(/,/g, "") || 0);
  
  // 연결된 항목(퇴직금, 사회보험)인 경우 정확한 총액 계산
  if (item.isLinkedItem && item.hb_pay_rel && item.hb_pay_rel_manual !== 'Y') {
    // 연결된 급여 항목 찾기
    const salaryItem = processedData.value.find(
      row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
    );
    
    if (salaryItem) {
      const salaryUnitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
      const salaryCount = Number(salaryItem.count?.replace(/,/g, "") || 0);
      const salaryMonths = Number(salaryItem.months?.replace(/,/g, "") || 0);
      const isCeo = (salaryItem.description || '').includes('(대표자)');
      
      // 퇴직금인 경우: 정확한 총액 = 월 총 급여 × 개월 / 12
      if (RETIREMENT_SCODES.includes(item.fc_scode)) {
        return isCeo ? 0 : Math.round((salaryUnitPrice * salaryCount * salaryMonths) / 12);
      }
      
      // 사회보험인 경우: 정확한 총액 = 월 총 급여 × 요율들의 합 × 개월
      if (INSURANCE_SCODES.includes(item.fc_scode) && props.taxData) {
        const totalMonthlySalary = salaryUnitPrice * salaryCount;
        
        const nationalPension = Math.round(totalMonthlySalary * (props.taxData.tr_yeon || 0));
        const healthInsurance = Math.round(totalMonthlySalary * (props.taxData.tr_gun || 0));
        const longTermCare = Math.round(healthInsurance * (props.taxData.tr_jang || 0));
        const employmentInsurance = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_go || 0));
        const unemployment = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_sil || 0));
        const industrialAccident = isCeo ? 0 : Math.round(totalMonthlySalary * (props.taxData.tr_san || 0));
        
        const monthlyInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
        return monthlyInsurance * salaryMonths;
      }
    }
  }
  
  // 일반 항목: 단순 계산
  return unitPrice * count * months;
};

// 관(L코드)별 예산액 합계 계산 (실시간)
const getLcodeBudgetSum = (item: ProcessedFinanceCode) => {
  const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}` === lcodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// 항(M코드)별 예산액 합계 계산 (실시간)
const getMcodeBudgetSum = (item: ProcessedFinanceCode) => {
  const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}` === mcodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// 목(S코드)별 예산액 합계 계산 (실시간)
const getScodeBudgetSum = (item: ProcessedFinanceCode) => {
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// rowspan만 재계산하는 함수
const recalculateRowspans = () => {
  const data = processedData.value;
  
  // rowspan 카운트
  const trcodeGroups: { [key: string]: number } = {};
  const lcodeGroups: { [key: string]: number } = {};
  const mcodeGroups: { [key: string]: number } = {};
  const scodeGroups: { [key: string]: number } = {};

  data.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    trcodeGroups[trcodeKey] = (trcodeGroups[trcodeKey] || 0) + 1;
    lcodeGroups[lcodeKey] = (lcodeGroups[lcodeKey] || 0) + 1;
    mcodeGroups[mcodeKey] = (mcodeGroups[mcodeKey] || 0) + 1;
    scodeGroups[scodeKey] = (scodeGroups[scodeKey] || 0) + 1;
  });

  // 표시 여부 및 rowspan 업데이트
  let prevTrcode = "";
  let prevLcode = "";
  let prevMcode = "";
  let prevScode = "";
  let trcodeCount = 0;
  let lcodeCount = 0;
  let mcodeCount = 0;
  let scodeCount = 0;

  data.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    const showTrcode = item.fc_trcode !== prevTrcode;
    const showLcode = lcodeKey !== `${prevTrcode}-${prevLcode}`;
    const showMcode = mcodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}`;
    const showScode = scodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}-${prevScode}`;

    if (showTrcode) {
      prevTrcode = item.fc_trcode;
      trcodeCount = 0;
    }

    if (showLcode) {
      prevLcode = item.fc_lcode;
      lcodeCount = 0;
    }

    if (showMcode) {
      prevMcode = item.fc_mcode;
      mcodeCount = 0;
    }

    if (showScode) {
      prevScode = item.fc_scode;
      scodeCount = 0;
    }

    item.showTrcode = trcodeCount === 0;
    item.showLcode = lcodeCount === 0;
    item.showMcode = mcodeCount === 0;
    item.showScode = scodeCount === 0;
    item.trcodeRowspan = trcodeGroups[trcodeKey];
    item.lcodeRowspan = lcodeGroups[lcodeKey];
    item.mcodeRowspan = mcodeGroups[mcodeKey];
    item.scodeRowspan = scodeGroups[scodeKey];

    trcodeCount++;
    lcodeCount++;
    mcodeCount++;
    scodeCount++;
  });
};

// 급여 scode에 매칭되는 사회보험/퇴직금 scode 가져오기
const getLinkedScodes = (salaryScode: string): { insurance: string; retirement: string } | null => {
  if (salaryScode === '1111') {
    return { insurance: '1151', retirement: '1141' }; // 직접비
  } else if (salaryScode === '1112') {
    return { insurance: '1152', retirement: '1142' }; // 간접비
  }
  return null;
};

// 본인부담금(112) ↔ 장기요양급여수입(611) 짝 scode 가져오기
const getRevenuePairScode = (scode: string): string | null => {
  if (scode === '112') return '611';
  if (scode === '611') return '112';
  return null;
};

// 수입 쌍 연결 키 생성 (고유 키)
const generateRevenueRelKey = (): string => {
  return `rev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// scode로 해당하는 financeCode 찾기
const findFinanceCodeByScode = (trcode: string, scode: string): FinanceCode | undefined => {
  return financeCodes.value.find(fc => fc.fc_trcode === trcode && fc.fc_scode === scode);
};

// scode 그룹의 마지막 인덱스 찾기 헬퍼 함수
const findLastIndexOfScodeGroup = (scodeKey: string, startIndex: number = 0): number => {
  let lastIndex = -1;
  for (let i = startIndex; i < processedData.value.length; i++) {
    const row = processedData.value[i];
    const rowScodeKey = `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}`;
    if (rowScodeKey === scodeKey) {
      lastIndex = i;
    }
  }
  return lastIndex;
};

// 행 추가 (화면에만 추가, 저장 시 DB INSERT)
// 급여 scode인 경우 사회보험/퇴직금도 함께 생성하고 같은 pay_rel 키로 연결
const addRow = (index: number) => {
  const currentItem = processedData.value[index];
  const isSalaryScode = SALARY_SCODES.includes(currentItem.fc_scode);
  
  if (isSalaryScode) {
    // 급여 scode인 경우: 급여 + 사회보험 + 퇴직금을 함께 생성
    const payRelKey = generatePayRelKey();
    const linkedScodes = getLinkedScodes(currentItem.fc_scode);
    
    if (!linkedScodes) {
      console.error("연결 scode를 찾을 수 없습니다.");
      return;
    }
    
    // 연결된 financeCode 찾기
    const insuranceFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.insurance);
    const retirementFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.retirement);
    
    // 급여 행 생성 (hb_idx 없음 - 저장 시 INSERT)
    const salaryRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: true,
      isLinkedItem: false,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 사회보험 행 생성
    const insuranceRow: ProcessedFinanceCode = {
      fc_idx: insuranceFinanceCode?.fc_idx || 0,
      fc_trcode: 'OUT',
      fc_trname: '세출',
      fc_year: currentItem.fc_year,
      fc_lcode: insuranceFinanceCode?.fc_lcode || '',
      fc_lname: insuranceFinanceCode?.fc_lname || '',
      fc_mcode: insuranceFinanceCode?.fc_mcode || '',
      fc_mname: insuranceFinanceCode?.fc_mname || '',
      fc_scode: linkedScodes.insurance,
      fc_sname: insuranceFinanceCode?.fc_sname || '사회보험부담금',
      fc_funding: insuranceFinanceCode?.fc_funding || '',
      fc_funding_name: insuranceFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: true,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 퇴직금 행 생성
    const retirementRow: ProcessedFinanceCode = {
      fc_idx: retirementFinanceCode?.fc_idx || 0,
      fc_trcode: 'OUT',
      fc_trname: '세출',
      fc_year: currentItem.fc_year,
      fc_lcode: retirementFinanceCode?.fc_lcode || '',
      fc_lname: retirementFinanceCode?.fc_lname || '',
      fc_mcode: retirementFinanceCode?.fc_mcode || '',
      fc_mname: retirementFinanceCode?.fc_mname || '',
      fc_scode: linkedScodes.retirement,
      fc_sname: retirementFinanceCode?.fc_sname || '퇴직금',
      fc_funding: retirementFinanceCode?.fc_funding || '',
      fc_funding_name: retirementFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: true,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 급여 행 추가
    const salaryScodeKey = `${salaryRow.fc_trcode}-${salaryRow.fc_lcode}-${salaryRow.fc_mcode}-${salaryRow.fc_scode}`;
    const salaryInsertIndex = findLastIndexOfScodeGroup(salaryScodeKey, index);
    processedData.value.splice((salaryInsertIndex >= 0 ? salaryInsertIndex : index) + 1, 0, salaryRow);
    
    // 사회보험 행 추가
    const insuranceScodeKey = `${insuranceRow.fc_trcode}-${insuranceRow.fc_lcode}-${insuranceRow.fc_mcode}-${insuranceRow.fc_scode}`;
    const insuranceInsertIndex = findLastIndexOfScodeGroup(insuranceScodeKey);
    if (insuranceInsertIndex >= 0) {
      processedData.value.splice(insuranceInsertIndex + 1, 0, insuranceRow);
    }
    
    // 퇴직금 행 추가
    const retirementScodeKey = `${retirementRow.fc_trcode}-${retirementRow.fc_lcode}-${retirementRow.fc_mcode}-${retirementRow.fc_scode}`;
    const retirementInsertIndex = findLastIndexOfScodeGroup(retirementScodeKey);
    if (retirementInsertIndex >= 0) {
      processedData.value.splice(retirementInsertIndex + 1, 0, retirementRow);
    }
    
    recalculateRowspans();
    
  } else if (currentItem.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(currentItem.fc_scode)) {
    // 본인부담금(112) 또는 장기요양급여수입(611) 추가 시 짝도 함께 생성 (세입만)
    const revenueRelKey = generateRevenueRelKey();
    const pairScode = getRevenuePairScode(currentItem.fc_scode);
    
    if (!pairScode) {
      console.error("짝 scode를 찾을 수 없습니다.");
      return;
    }
    
    const pairFinanceCode = findFinanceCodeByScode('IN', pairScode);
    
    // 현재 항목 행 생성
    const currentRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: revenueRelKey,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: true,
      suga_rank: '180분',
      suga_times: '20',
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 짝 항목 행 생성
    const pairRow: ProcessedFinanceCode = {
      fc_idx: pairFinanceCode?.fc_idx || 0,
      fc_trcode: 'IN',
      fc_trname: '세입',
      fc_year: currentItem.fc_year,
      fc_lcode: pairFinanceCode?.fc_lcode || '',
      fc_lname: pairFinanceCode?.fc_lname || '',
      fc_mcode: pairFinanceCode?.fc_mcode || '',
      fc_mname: pairFinanceCode?.fc_mname || '',
      fc_scode: pairScode,
      fc_sname: pairFinanceCode?.fc_sname || (pairScode === '112' ? '본인부담금수입' : '장기요양급여수입'),
      fc_funding: pairFinanceCode?.fc_funding || '',
      fc_funding_name: pairFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: revenueRelKey,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: true,
      suga_rank: '180분',
      suga_times: '20',
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 현재 항목 행 추가
    const currentScodeKey = `${currentRow.fc_trcode}-${currentRow.fc_lcode}-${currentRow.fc_mcode}-${currentRow.fc_scode}`;
    const currentInsertIndex = findLastIndexOfScodeGroup(currentScodeKey, index);
    processedData.value.splice((currentInsertIndex >= 0 ? currentInsertIndex : index) + 1, 0, currentRow);
    
    // 짝 항목 행 추가
    const pairScodeKey = `${pairRow.fc_trcode}-${pairRow.fc_lcode}-${pairRow.fc_mcode}-${pairRow.fc_scode}`;
    const pairInsertIndex = findLastIndexOfScodeGroup(pairScodeKey);
    if (pairInsertIndex >= 0) {
      processedData.value.splice(pairInsertIndex + 1, 0, pairRow);
    }
    
    recalculateRowspans();
    
  } else {
    // 일반 행 추가
    const scodeKey = `${currentItem.fc_trcode}-${currentItem.fc_lcode}-${currentItem.fc_mcode}-${currentItem.fc_scode}`;
    const insertIndex = findLastIndexOfScodeGroup(scodeKey, index);
    
    const newRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    processedData.value.splice((insertIndex >= 0 ? insertIndex : index) + 1, 0, newRow);
    recalculateRowspans();
  }
};

// 같은 목 코드를 가진 행의 개수 확인
const getSameScodeRowCount = (item: ProcessedFinanceCode) => {
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  return processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey
  ).length;
};

// 삭제 가능 여부 확인 (연결 항목만 개별 삭제 불가)
const canDeleteRow = (item: ProcessedFinanceCode) => {
  // 연결된 항목(사회보험/퇴직금)은 개별 삭제 불가 (급여와 함께 삭제됨)
  if (item.isLinkedItem) {
    return false;
  }
  // 나머지 항목은 삭제 가능 (마지막 행이면 데이터만 초기화)
  return true;
};

// 삭제 대기 목록에 추가 (저장 시 실제 DB 삭제)
const addToPendingDelete = (hbIdx: number) => {
  if (hbIdx && !pendingDeleteIds.value.includes(hbIdx)) {
    pendingDeleteIds.value.push(hbIdx);
  }
};

// 행 데이터 초기화 (마지막 행일 때 사용)
const clearRowData = (item: ProcessedFinanceCode) => {
  // 삭제 대기 목록에 추가
  if (item.hb_idx) {
    addToPendingDelete(item.hb_idx);
  }
  // 데이터 초기화
  item.hb_idx = undefined;
  item.description = '';
  item.unit_price = '';
  item.count = '';
  item.months = '';
  item.hb_pay_rel = undefined;
  item.hb_pay_rel_manual = 'N';
  item.hb_revenue_rel = undefined;
  item.suga_rank = item.isRevenuePairItem ? '180분' : undefined;
  item.suga_times = item.isRevenuePairItem ? '20' : undefined;
};

// 급여 항목의 같은 scode 내 순서 인덱스 가져오기
const getSalaryIndexInScode = (item: ProcessedFinanceCode): number => {
  if (!item.isSalaryItem) return -1;
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  const sameScodeItems = processedData.value.filter(
    row => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey && row.isSalaryItem
  );
  return sameScodeItems.findIndex(row => row === item);
};

// 순서 기반으로 연결된 사회보험/퇴직금 항목 찾기
const findLinkedItemsByOrder = (salaryItem: ProcessedFinanceCode): ProcessedFinanceCode[] => {
  const linkedItems: ProcessedFinanceCode[] = [];
  const salaryIndex = getSalaryIndexInScode(salaryItem);
  if (salaryIndex < 0) return linkedItems;
  
  const linkedScodes = getLinkedScodes(salaryItem.fc_scode);
  if (!linkedScodes) return linkedItems;
  
  // 사회보험 scode의 같은 순서 항목 찾기
  const insuranceScodeKey = `OUT-${linkedScodes.insurance}`;
  let insuranceCount = 0;
  for (const row of processedData.value) {
    if (row.fc_trcode === 'OUT' && row.fc_scode === linkedScodes.insurance && row.isLinkedItem) {
      if (insuranceCount === salaryIndex) {
        linkedItems.push(row);
        break;
      }
      insuranceCount++;
    }
  }
  
  // 퇴직금 scode의 같은 순서 항목 찾기
  let retirementCount = 0;
  for (const row of processedData.value) {
    if (row.fc_trcode === 'OUT' && row.fc_scode === linkedScodes.retirement && row.isLinkedItem) {
      if (retirementCount === salaryIndex) {
        linkedItems.push(row);
        break;
      }
      retirementCount++;
    }
  }
  
  return linkedItems;
};

// 순서 기반으로 수입 쌍 짝궁 찾기 (기존 getRevenuePairItem 사용)
// getRevenuePairItem 함수가 이미 순서 기반으로 동작함

// 행 삭제 또는 데이터 초기화 (저장 시 실제 DB 반영)
// - 마지막 행이면 데이터만 초기화
// - 급여 삭제 시 순서 기반으로 사회보험/퇴직금도 함께 삭제
// - 수입 쌍 삭제 시 순서 기반으로 짝도 함께 삭제
const deleteRow = (index: number) => {
  const item = processedData.value[index];
  
  // 연결 항목은 개별 삭제 불가
  if (item.isLinkedItem) {
    return;
  }
  
  const isLastRow = getSameScodeRowCount(item) === 1;
  
  // 1. 급여 항목인 경우: 연결된 사회보험/퇴직금도 함께 처리
  if (item.isSalaryItem) {
    // hb_pay_rel이 있으면 같은 키로 찾기, 없으면 순서 기반으로 찾기
    let linkedItems: ProcessedFinanceCode[] = [];
    
    if (item.hb_pay_rel) {
      linkedItems = processedData.value.filter(
        row => row.hb_pay_rel === item.hb_pay_rel && row !== item
      );
    } else {
      linkedItems = findLinkedItemsByOrder(item);
    }
    
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
      // 연결된 항목들도 데이터 초기화
      for (const linkedItem of linkedItems) {
        const linkedIsLast = getSameScodeRowCount(linkedItem) === 1;
        if (linkedIsLast) {
          clearRowData(linkedItem);
        } else {
          // 연결 항목의 scode에 여러 행이 있으면 삭제
          if (linkedItem.hb_idx) {
            addToPendingDelete(linkedItem.hb_idx);
          }
          const linkedIndex = processedData.value.indexOf(linkedItem);
          if (linkedIndex >= 0) {
            processedData.value.splice(linkedIndex, 1);
          }
        }
      }
    } else {
      // 마지막 행이 아니면 삭제
      // 연결된 항목들 먼저 삭제 (역순으로)
      for (const linkedItem of linkedItems) {
        if (linkedItem.hb_idx) {
          addToPendingDelete(linkedItem.hb_idx);
        }
        const linkedIndex = processedData.value.indexOf(linkedItem);
        if (linkedIndex >= 0) {
          processedData.value.splice(linkedIndex, 1);
        }
      }
      // 급여 항목 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      // 인덱스가 변경되었을 수 있으므로 다시 찾기
      const currentIndex = processedData.value.indexOf(item);
      if (currentIndex >= 0) {
        processedData.value.splice(currentIndex, 1);
      }
    }
  }
  // 2. 수입 쌍(112/611) 항목인 경우: 짝도 함께 처리
  else if (isRevenuePairItem(item)) {
    // 순서 기반으로 짝 찾기
    const pairItem = getRevenuePairItem(item);
    
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
      // 짝 항목도 처리
      if (pairItem) {
        const pairIsLast = getSameScodeRowCount(pairItem) === 1;
        if (pairIsLast) {
          clearRowData(pairItem);
        } else {
          if (pairItem.hb_idx) {
            addToPendingDelete(pairItem.hb_idx);
          }
          const pairIndex = processedData.value.indexOf(pairItem);
          if (pairIndex >= 0) {
            processedData.value.splice(pairIndex, 1);
          }
        }
      }
    } else {
      // 마지막 행이 아니면 삭제
      // 짝 항목 먼저 삭제
      if (pairItem) {
        if (pairItem.hb_idx) {
          addToPendingDelete(pairItem.hb_idx);
        }
        const pairIndex = processedData.value.indexOf(pairItem);
        if (pairIndex >= 0) {
          processedData.value.splice(pairIndex, 1);
        }
      }
      // 현재 항목 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      const currentIndex = processedData.value.indexOf(item);
      if (currentIndex >= 0) {
        processedData.value.splice(currentIndex, 1);
      }
    }
  }
  // 3. 일반 항목
  else {
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
    } else {
      // 마지막 행이 아니면 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      processedData.value.splice(index, 1);
    }
  }
  
  // rowspan 재계산
  recalculateRowspans();
};

// 첫 예산 작성 폼 입력 핸들러
const onCaregiverCountInput = (event: any) => {
  const value = event.target.value.replace(/[^\d]/g, "");
  firstBudgetData.value.caregiverCount = value;
};

const onPatientCountInput = (event: any) => {
  const value = event.target.value.replace(/[^\d]/g, "");
  firstBudgetData.value.patientCount = value;
};

const onBudgetMonthsInput = (event: any) => {
  const value = event.target.value.replace(/[^\d]/g, "");
  if (value) {
    const num = parseInt(value);
    if (num > 12) {
      firstBudgetData.value.budgetMonths = "12";
    } else if (num < 1) {
      firstBudgetData.value.budgetMonths = "1";
    } else {
      firstBudgetData.value.budgetMonths = value;
    }
  } else {
    firstBudgetData.value.budgetMonths = "";
  }
};

const onSugaTimeChange = () => {
  // 수가 선택 변경 시 처리 (필요시)
};

// 현재 단계 유효성 검사
const isCurrentStepValid = computed(() => {
  switch (currentStep.value) {
    case 1:
      return firstBudgetData.value.patientCount && parseInt(firstBudgetData.value.patientCount) > 0;
    case 2:
      return firstBudgetData.value.caregiverCount && parseInt(firstBudgetData.value.caregiverCount) > 0;
    case 3:
      return firstBudgetData.value.selectedSugaTime !== "";
    case 4:
      return firstBudgetData.value.budgetMonths && parseInt(firstBudgetData.value.budgetMonths) > 0 && parseInt(firstBudgetData.value.budgetMonths) <= 12;
    default:
      return false;
  }
});

// 첫 예산 작성 폼 유효성 검사
const isFirstBudgetFormValid = computed(() => {
  return (
    firstBudgetData.value.caregiverCount &&
    parseInt(firstBudgetData.value.caregiverCount) > 0 &&
    firstBudgetData.value.patientCount &&
    parseInt(firstBudgetData.value.patientCount) > 0 &&
    firstBudgetData.value.selectedSugaTime &&
    firstBudgetData.value.budgetMonths &&
    parseInt(firstBudgetData.value.budgetMonths) > 0
  );
});

// 세입 예산 데이터 존재 여부 확인
const hasRevenueData = computed(() => {
  // processedData에서 세입(IN) 데이터 중 실제 예산이 입력된 행이 있는지 확인
  return processedData.value.some(item => {
    if (item.fc_trcode !== 'IN') return false;
    const unitPrice = Number(item.unit_price?.replace(/,/g, '') || 0);
    const count = Number(item.count?.replace(/,/g, '') || 0);
    const months = Number(item.months?.replace(/,/g, '') || 0);
    return unitPrice > 0 || count > 0 || months > 0;
  });
});

// 예산 스타터 화면 표시 여부
const showBudgetStarterPrompt = computed(() => {
  return !loading.value && !hasRevenueData.value;
});

// 추경차수 라벨
const budgetClassLabel = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return '본예산';
  return `${budClass}차 추경`;
});

// 추경차수 스타일
const budgetClassStyle = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'primary';
  return 'warning';
});

// 추경차수 아이콘
const budgetClassIcon = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'mdi-file-document-outline';
  return 'mdi-file-document-edit-outline';
});

// 연도 범위
const currentSystemYear = new Date().getFullYear();
const minYear = currentSystemYear - 2;
const maxYear = currentSystemYear + 1;

// 연도 변경 함수
const changeYear = (delta: number) => {
  const newYear = parseInt(searchParams.value.lebYear) + delta;
  if (newYear >= minYear && newYear <= maxYear) {
    searchParams.value.lebYear = newYear.toString();
    emit('year-change', searchParams.value.lebYear);
    fetchFinanceCodes();
  }
};

// 금액 포맷팅 함수
const formatMoney = (amount: number): string => {
  return new Intl.NumberFormat('ko-KR').format(amount);
};

// 세입 합계
const totalRevenue = computed(() => {
  let total = 0;
  processedData.value.forEach(item => {
    if (item.fc_trcode === 'IN') {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 세출 합계
const totalExpense = computed(() => {
  let total = 0;
  processedData.value.forEach(item => {
    if (item.fc_trcode === 'OUT') {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 잔액
const balance = computed(() => {
  return totalRevenue.value - totalExpense.value;
});

// 기준 인건비 비율 (props에서 가져오거나 기본값 60%)
const standardLaborCostRate = computed(() => {
  return props.taxData?.tr_labor_cost_rate || 60;
});

// 인건비 총액 계산 (직접비: 급여1111 + 사회보험1151 + 퇴직금1141)
const totalLaborCost = computed(() => {
  let total = 0;
  processedData.value.forEach(item => {
    // 직접비 급여, 사회보험, 퇴직금만 포함
    if (['1111', '1151', '1141'].includes(item.fc_scode)) {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 인건비 비율 계산용 수입 (본인부담금112 + 장기요양급여611 + 가산금613)
const laborCostBaseRevenue = computed(() => {
  let total = 0;
  processedData.value.forEach(item => {
    if (['112', '611', '613'].includes(item.fc_scode)) {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 현재 인건비 비율
const currentLaborCostRate = computed(() => {
  if (laborCostBaseRevenue.value === 0) return 0;
  return (totalLaborCost.value / laborCostBaseRevenue.value) * 100;
});

// 인건비 비율 기준 미달 여부
const isLaborCostBelowStandard = computed(() => {
  return currentLaborCostRate.value < standardLaborCostRate.value;
});

// 인건비 데이터 존재 여부 (급여 항목이 있는지 확인)
const hasLaborCostData = computed(() => {
  return processedData.value.some(item => 
    ['1111', '1112'].includes(item.fc_scode) && 
    item.description && 
    item.unit_price
  );
});

// 임직원보수일람표 모달 열기
const downloadPayrollList = () => {
  showPayExcelDialog.value = true;
};

// 희망이음 예산업로드 모달 열기
const downloadHopeExcel = () => {
  showHopeExcelDialog.value = true;
};

// 예산 삭제 함수
const deleteBudget = async () => {
  if (!store.state.user?.m_sisul_code) {
    console.error("시설 코드가 없습니다.");
    return;
  }

  deleting.value = true;
  try {
    const response = await axios.post("/api/budget/delete", {
      sisulCode: store.state.user.m_sisul_code,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || '' // 빈 문자열 = 본예산
    });

    if (response.data.success) {
      // 삭제 후 데이터 다시 조회
      showDeleteDialog.value = false;
      await fetchFinanceCodes();
      showSnackbar(`${searchParams.value.lebYear}년 ${budgetClassLabel.value} 예산이 삭제되었습니다.`, 'success');
    } else {
      console.error("예산 삭제 실패:", response.data.message);
      showSnackbar('예산 삭제에 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error("예산 삭제 오류:", error);
    showSnackbar('예산 삭제 중 오류가 발생했습니다.', 'error');
  } finally {
    deleting.value = false;
  }
};

// 예산 작성 마법사 열기
const openBudgetWizard = () => {
  // Q&A 스타터 페이지로 이동
  emit('open-starter');
};

// 예산 작성 마법사 닫기
const closeBudgetWizard = () => {
  showBudgetWizard.value = false;
  currentStep.value = 1;
};

// 다음 단계
const nextStep = () => {
  if (currentStep.value < totalSteps && isCurrentStepValid.value) {
    currentStep.value++;
  }
};

// 이전 단계
const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

// 첫 예산 생성
const createFirstBudget = async () => {
  if (!isFirstBudgetFormValid.value) {
    return;
  }

  saving.value = true;
  try {
    // TODO: 첫 예산 생성 로직 구현
    // 여기서 입력된 정보를 바탕으로 예산 데이터를 생성해야 함
    
    // 예시: 생성 후 데이터 다시 조회
    await fetchFinanceCodes();
    
    // 마법사 닫기
    closeBudgetWizard();
  } catch (error) {
    console.error("첫 예산 생성 오류:", error);
  } finally {
    saving.value = false;
  }
};

// 급여 인풋 포커스 시 그룹 활성화
const handleSalaryFocus = (item: ProcessedFinanceCode) => {
  if (item.isSalaryItem && item.hb_pay_rel) {
    focusedPayRelGroup.value = item.hb_pay_rel;
  }
};

// 급여 인풋 블러 시 그룹 비활성화
const handleSalaryBlur = () => {
  focusedPayRelGroup.value = null;
};

// 본인부담금-장기요양급여 포커스 시 쌍 그룹 활성화 (순서 기반, 세입만)
const handleRevenuePairFocus = (item: ProcessedFinanceCode) => {
  if (isRevenuePairItem(item)) {
    focusedRevenuePairItem.value = item;
  }
};

// 본인부담금-장기요양급여 블러 시 쌍 그룹 비활성화
const handleRevenuePairBlur = () => {
  focusedRevenuePairItem.value = null;
};

// 본인부담금/장기요양급여 scode 확인 (세입에서만 유효)
const isRevenuePairScode = (scode: string, trcode?: string): boolean => {
  // trcode가 제공되면 세입(IN)인지 확인
  if (trcode && trcode !== 'IN') return false;
  return REVENUE_PAIR_SCODES.includes(scode);
};

// 항목이 수입 쌍인지 확인 (trcode도 함께 체크)
const isRevenuePairItem = (item: ProcessedFinanceCode): boolean => {
  return item.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(item.fc_scode);
};

// 수가 선택에 따른 단가 계산
const calculateSugaUnitPrice = (item: ProcessedFinanceCode): number => {
  if (!item.suga_rank || !item.suga_times || !props.sugaList) return 0;
  
  const suga = props.sugaList.find(s => s.suga_rank === item.suga_rank);
  if (!suga) return 0;
  
  const times = Number(item.suga_times.replace(/,/g, '')) || 0;
  
  // 본인부담금(112): suga_bonin × 횟수
  // 장기요양급여수입(611): (suga_money - suga_bonin) × 횟수
  if (item.fc_scode === '112') {
    return suga.suga_bonin * times;
  } else if (item.fc_scode === '611') {
    return (suga.suga_money - suga.suga_bonin) * times;
  }
  
  return 0;
};

// 수가 선택 변경 시 처리 (본인부담금 ↔ 장기요양급여 자동 연동)
const handleSugaChange = (item: ProcessedFinanceCode) => {
  // 단가 계산
  const unitPrice = calculateSugaUnitPrice(item);
  item.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : '';
  
  // description 업데이트
  item.description = item.suga_rank && item.suga_times ? `${item.suga_rank} X ${item.suga_times}` : '';
  
  // 본인부담금(112) ↔ 장기요양급여(611) 자동 연동 (세입만)
  if (isRevenuePairItem(item)) {
    syncRevenuePairValues(item, 'suga_rank');
    syncRevenuePairValues(item, 'suga_times');
  }
};

// 수가 횟수 입력 변경 시 처리 (본인부담금 ↔ 장기요양급여 자동 연동)
const handleSugaTimesInput = (event: any, item: ProcessedFinanceCode) => {
  // 숫자만 허용
  const value = event.target.value.replace(/[^0-9]/g, '');
  item.suga_times = value;
  
  // 단가 재계산
  const unitPrice = calculateSugaUnitPrice(item);
  item.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : '';
  
  // description 업데이트
  item.description = item.suga_rank && item.suga_times ? `${item.suga_rank} X ${item.suga_times}` : '';
  
  // 본인부담금(112) ↔ 장기요양급여(611) 자동 연동 (세입만)
  if (isRevenuePairItem(item)) {
    syncRevenuePairValues(item, 'suga_times');
  }
};

// 본인부담금(112) 항목들의 인덱스 배열 가져오기
const getRevenue112Items = (): ProcessedFinanceCode[] => {
  return processedData.value.filter(row => row.fc_scode === '112');
};

// 장기요양급여(611) 항목들의 인덱스 배열 가져오기
const getRevenue611Items = (): ProcessedFinanceCode[] => {
  return processedData.value.filter(row => row.fc_scode === '611');
};

// 본인부담금/장기요양급여 항목의 순서(인덱스) 가져오기
const getRevenuePairIndex = (item: ProcessedFinanceCode): number => {
  if (item.fc_scode === '112') {
    return getRevenue112Items().findIndex(row => row === item);
  } else if (item.fc_scode === '611') {
    return getRevenue611Items().findIndex(row => row === item);
  }
  return -1;
};

// 짝궁 항목 가져오기 (순서 기반, 세입만)
const getRevenuePairItem = (item: ProcessedFinanceCode): ProcessedFinanceCode | null => {
  if (!isRevenuePairItem(item)) return null;
  
  const index = getRevenuePairIndex(item);
  if (index < 0) return null;
  
  const pairItems = item.fc_scode === '112' ? getRevenue611Items() : getRevenue112Items();
  return pairItems[index] || null;
};

// 포커스된 수입 쌍 정보 저장 (항목 자체 참조)
const focusedRevenuePairItem = ref<ProcessedFinanceCode | null>(null);

// 해당 행이 현재 포커스된 수입 쌍의 짝궁인지 확인
const isRevenuePairHighlighted = (item: ProcessedFinanceCode): boolean => {
  if (!focusedRevenuePairItem.value) return false;
  if (!isRevenuePairItem(item)) return false;
  
  // 포커스된 항목의 짝궁인지 확인
  const pairItem = getRevenuePairItem(focusedRevenuePairItem.value);
  return pairItem === item;
};

// 본인부담금-장기요양급여 쌍 항목의 count, months, suga_rank, suga_times 연동
// 순서 기반으로 연동 (112 N번째 ↔ 611 N번째)
const syncRevenuePairValues = (item: ProcessedFinanceCode, field: 'count' | 'months' | 'suga_rank' | 'suga_times') => {
  // 본인부담금(112) 또는 장기요양급여(611)인지 확인 (세입만)
  if (!isRevenuePairItem(item)) return;
  
  // 순서 기반으로 짝궁 찾기
  const pairItem = getRevenuePairItem(item);
  if (!pairItem) return;
  
  const newValue = item[field];
  
  // @ts-ignore - dynamic field assignment
  pairItem[field] = newValue;
  
  // suga_rank나 suga_times가 변경되면 unit_price도 재계산
  if (field === 'suga_rank' || field === 'suga_times') {
    const unitPrice = calculateSugaUnitPrice(pairItem);
    pairItem.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : '';
    pairItem.description = pairItem.suga_rank && pairItem.suga_times ? `${pairItem.suga_rank} X ${pairItem.suga_times}` : '';
  }
};

// 본인부담금-장기요양급여 입력 변경 핸들러
const handleRevenuePairInput = (event: any, item: ProcessedFinanceCode, field: string) => {
  // 기존 숫자 포맷팅 적용
  formatNumber(event, item, field);
  
  // 본인부담금/장기요양급여 항목이고 count나 months 변경 시 쌍 항목도 연동 (세입만)
  if (isRevenuePairItem(item) && (field === 'count' || field === 'months')) {
    syncRevenuePairValues(item, field as 'count' | 'months');
  }
};

// 급여 종류 변경 시 연결된 항목 description 업데이트 및 재계산
const handleSalaryTypeChange = async (salaryItem: ProcessedFinanceCode) => {
  if (!salaryItem.isSalaryItem) return;
  
  const salaryType = salaryItem.description || '';
  const isCeo = salaryType.includes('(대표자)');
  
  // 급여명 기본 부분 추출 (예: "센터장 급여" → "센터장", "사회복지사(대표자) 급여" → "사회복지사(대표자)")
  const baseName = salaryType.replace(' 급여', '');
  
  // 연결된 항목들의 description 업데이트 및 대표자 처리
  if (salaryItem.hb_pay_rel) {
    const payRelKey = salaryItem.hb_pay_rel;
    const linkedScodes = getLinkedScodes(salaryItem.fc_scode);
    
    if (isCeo && linkedScodes) {
      // 대표자인 경우: 퇴직금 행 삭제 (저장 시 실제 DB 반영)
      for (let i = processedData.value.length - 1; i >= 0; i--) {
        const item = processedData.value[i];
        if (item.hb_pay_rel === payRelKey && RETIREMENT_SCODES.includes(item.fc_scode)) {
          // 삭제 대기 목록에 추가
          if (item.hb_idx) {
            addToPendingDelete(item.hb_idx);
          }
          processedData.value.splice(i, 1);
        }
      }
    } else if (!isCeo && linkedScodes) {
      // 비대표자인 경우: 퇴직금 행이 없으면 생성
      const hasRetirement = processedData.value.some(
        item => item.hb_pay_rel === payRelKey && RETIREMENT_SCODES.includes(item.fc_scode)
      );
      
      if (!hasRetirement && store.state.user?.m_sisul_code) {
        // 퇴직금 행 INSERT
        const retirementResponse = await axios.post("/api/budget/insert", {
          sisulCode: store.state.user.m_sisul_code,
          year: searchParams.value.lebYear,
          budClass: props.budgetClass || '',
          trcode: 'OUT',
          scode: linkedScodes.retirement,
          payRel: payRelKey,
        });
        
        if (retirementResponse.data.success && retirementResponse.data.hb_idx) {
          const retirementFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.retirement);
          
          const retirementRow: ProcessedFinanceCode = {
            fc_idx: retirementFinanceCode?.fc_idx || 0,
            fc_trcode: 'OUT',
            fc_trname: '세출',
            fc_year: salaryItem.fc_year,
            fc_lcode: retirementFinanceCode?.fc_lcode || '',
            fc_lname: retirementFinanceCode?.fc_lname || '',
            fc_mcode: retirementFinanceCode?.fc_mcode || '',
            fc_mname: retirementFinanceCode?.fc_mname || '',
            fc_scode: linkedScodes.retirement,
            fc_sname: retirementFinanceCode?.fc_sname || '퇴직금',
            fc_funding: retirementFinanceCode?.fc_funding || '',
            fc_funding_name: retirementFinanceCode?.fc_funding_name || '',
            hb_idx: retirementResponse.data.hb_idx,
            hb_pay_rel: payRelKey,
            hb_pay_rel_manual: 'N',
            hb_revenue_rel: undefined,
            budget_amount: "",
            description: `${baseName} 퇴직금`,
            unit_price: "",
            count: "",
            months: "",
            isAddedRow: true,
            isSalaryItem: false,
            isLinkedItem: true,
            isRevenuePairItem: false,
            showTrcode: false,
            showLcode: false,
            showMcode: false,
            showScode: false,
            trcodeRowspan: 0,
            lcodeRowspan: 0,
            mcodeRowspan: 0,
            scodeRowspan: 0,
          };
          
          // 퇴직금 scode 그룹의 마지막에 추가
          const retirementScodeKey = `${retirementRow.fc_trcode}-${retirementRow.fc_lcode}-${retirementRow.fc_mcode}-${retirementRow.fc_scode}`;
          let retirementInsertIndex = -1;
          for (let i = 0; i < processedData.value.length; i++) {
            const row = processedData.value[i];
            const rowScodeKey = `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}`;
            if (rowScodeKey === retirementScodeKey) {
              retirementInsertIndex = i;
            }
          }
          if (retirementInsertIndex >= 0) {
            processedData.value.splice(retirementInsertIndex + 1, 0, retirementRow);
          }
        }
      }
    }
    
    // 연결된 항목들의 description 업데이트
    processedData.value.forEach(item => {
      if (item.hb_pay_rel !== payRelKey) return;
      if (item.isSalaryItem) return; // 급여 자체는 제외
      
      const isInsurance = INSURANCE_SCODES.includes(item.fc_scode);
      const isRetirement = RETIREMENT_SCODES.includes(item.fc_scode);
      
      if (isInsurance) {
        item.description = `${baseName} 사회보험부담금`;
      } else if (isRetirement) {
        item.description = `${baseName} 퇴직금`;
      }
    });
    
    // rowspan 재계산
    recalculateRowspans();
    
    // 대표자 여부에 따라 재계산
    recalculateLinkedItems(salaryItem);
  }
};

// 대표자 여부 확인 헬퍼
const isCeoSalary = (item: ProcessedFinanceCode): boolean => {
  return !!item.isSalaryItem && (item.description || '').includes('(대표자)');
};

// 해당 행이 현재 포커스된 급여 그룹의 연결 항목인지 확인
const isLinkedHighlighted = (item: ProcessedFinanceCode): boolean => {
  if (!focusedPayRelGroup.value || !item.hb_pay_rel) return false;
  // 급여 항목이 아닌 연결된 항목만 하이라이트
  return item.hb_pay_rel === focusedPayRelGroup.value && !!item.isLinkedItem;
};

// 해당 행이 연결된 항목(수정 불가)인지 확인
// hb_pay_rel_manual='Y'이면 수동 편집 모드이므로 수정 가능
const isLinkedReadonly = (item: ProcessedFinanceCode): boolean => {
  return !!item.isLinkedItem && item.hb_pay_rel_manual !== 'Y';
};

// 잠금 해제 토글
const toggleManualEdit = (item: ProcessedFinanceCode) => {
  if (!item.isLinkedItem) return;
  
  if (item.hb_pay_rel_manual === 'Y') {
    // 수동 → 자동으로 전환: 현재 수동 입력 값을 저장
    item.manual_saved_unit_price = item.unit_price;
    item.manual_saved_count = item.count;
    item.manual_saved_months = item.months;
    item.hb_pay_rel_manual = 'N';
    
    // 자동계산 모드로 전환 시 연결된 급여 기반으로 재계산
    if (item.hb_pay_rel) {
      const salaryItem = processedData.value.find(
        row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
      );
      if (salaryItem) {
        recalculateLinkedItems(salaryItem);
      }
    }
  } else {
    // 자동 → 수동으로 전환: 저장된 수동 입력 값이 있으면 복원
    item.hb_pay_rel_manual = 'Y';
    if (item.manual_saved_unit_price !== undefined) {
      item.unit_price = item.manual_saved_unit_price;
    }
    if (item.manual_saved_count !== undefined) {
      item.count = item.manual_saved_count;
    }
    if (item.manual_saved_months !== undefined) {
      item.months = item.manual_saved_months;
    }
  }
};

// 급여 변경 시 연결된 사회보험/퇴직금 자동 재계산
const recalculateLinkedItems = (salaryItem: ProcessedFinanceCode) => {
  if (!salaryItem.hb_pay_rel || !salaryItem.isSalaryItem) return;
  if (!props.taxData) return;
  
  const payRelId = salaryItem.hb_pay_rel;
  
  // 대표자 여부 확인
  const isCeo = (salaryItem.description || '').includes('(대표자)');
  
  // 급여 정보 계산 (1인당 단가 기준)
  const unitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
  const count = Number(salaryItem.count?.replace(/,/g, "") || 0);
  const months = Number(salaryItem.months?.replace(/,/g, "") || 0);
  
  // 연결된 사회보험/퇴직금 항목 찾아서 재계산
  processedData.value.forEach(item => {
    if (item.hb_pay_rel !== payRelId) return;
    if (item.isSalaryItem) return; // 급여 자체는 제외
    
    // 수동 편집 모드('Y')인 항목은 자동 재계산 제외
    if (item.hb_pay_rel_manual === 'Y') return;
    
    const isInsurance = INSURANCE_SCODES.includes(item.fc_scode);
    const isRetirement = RETIREMENT_SCODES.includes(item.fc_scode);
    
    if (isInsurance && props.taxData) {
      // 사회보험부담금 계산 (1인당 기준)
      const nationalPension = Math.round(unitPrice * (props.taxData.tr_yeon || 0));
      const healthInsurance = Math.round(unitPrice * (props.taxData.tr_gun || 0));
      const longTermCare = Math.round(healthInsurance * (props.taxData.tr_jang || 0));
      
      // 대표자인 경우 고용보험, 실업급여, 산재보험 제외
      const employmentInsurance = isCeo ? 0 : Math.round(unitPrice * (props.taxData.tr_go || 0));
      const unemployment = isCeo ? 0 : Math.round(unitPrice * (props.taxData.tr_sil || 0));
      const industrialAccident = isCeo ? 0 : Math.round(unitPrice * (props.taxData.tr_san || 0));
      
      // 1인당 월 사회보험부담금
      const perPersonInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
      
      item.unit_price = perPersonInsurance.toLocaleString();
      item.count = count.toLocaleString(); // 급여의 명(회)와 동일
      item.months = months.toLocaleString(); // 급여의 개월과 동일
    } else if (isRetirement) {
      // 대표자인 경우 퇴직금 0원, 아니면 1인당 월 퇴직금 (급여의 1/12)
      const perPersonRetirement = isCeo ? 0 : Math.round(unitPrice / 12);
      
      item.unit_price = perPersonRetirement.toLocaleString();
      item.count = count.toLocaleString(); // 급여의 명(회)와 동일
      item.months = months.toLocaleString(); // 급여의 개월과 동일
    }
  });
};

// 급여 입력 변경 핸들러 (자동 재계산 트리거)
const handleSalaryInput = (event: any, item: ProcessedFinanceCode, field: string) => {
  // 기존 숫자 포맷팅 적용
  formatNumber(event, item, field);
  
  // 급여 항목이면 연결된 사회보험/퇴직금 재계산
  if (item.isSalaryItem && item.hb_pay_rel) {
    recalculateLinkedItems(item);
  }
};

// props.year 변경 시 동기화
watch(() => props.year, (newYear) => {
  if (newYear && newYear !== searchParams.value.lebYear) {
    searchParams.value.lebYear = newYear;
  }
}, { immediate: true });

// 연도 또는 구분 변경 시 자동 조회 및 emit
watch([() => searchParams.value.lebYear, selectedTrcode], ([newYear], [oldYear]) => {
  // 연도가 변경되었으면 emit
  if (newYear !== oldYear) {
    emit('year-change', searchParams.value.lebYear);
  }
  fetchFinanceCodes();
});

// 초기 로드
onMounted(async () => {
  await fetchFinanceCodes();
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/pgmBudget.scss";
</style>
